<template>
	<div class="poll-card" :class="{ closed: isClosed, disabled }">
		<div class="poll-card-header">
			<div
				class="poll-card-question-container"
				:class="{ translated: poll?.isTranslated }"
			>
				<span class="poll-card-question" v-safe-html="displayedQuestion" />
				<span v-if="canTranslate && !highlights">{{ '\xa0' }}</span>
				<TranslationGlobe
					v-if="canTranslate && !highlights"
					class="poll-translation-globe"
					:lang="poll?.language"
					:translated="poll?.isTranslated"
					@click="onGlobeClick"
				/>
			</div>
			<div class="poll-card-details">
				<div
					v-if="!isClosed"
					v-text="
						poll.allows_multiple_votes
							? t('polls_instruction_select_multiple')
							: t('polls_instruction_select_single')
					"
				/>
				<div v-if="!isClosed" class="poll-card-details-divider" />
				<PollCloseState
					:pollId="pollId"
					@poll-change="onPollChange"
					@state-change="onStateChange"
				/>
			</div>
			<div class="poll-card-anonymous" v-if="isAnonymous">
				<EyeSlashIcon type="regular" /> {{ t('polls_type_anonymous') }}
			</div>
		</div>
		<RadioSelect
			class="poll-card-options"
			v-model="selectModel"
			:options="options"
			:multiple="poll.allows_multiple_votes"
			:disabled="disabled"
			:hide-icons="isClosed"
			@change="onVoteChange"
		>
			<template #option="{ option }">
				<div class="poll-card-option">
					<div class="poll-card-option-text">
						<div
							class="poll-card-option-label"
							:class="{ translated: isOptionTranslated(option) }"
							v-safe-html="getDisplayedOptionText(option)"
						/>
						<div class="poll-card-option-count" v-text="option.count" />
					</div>
					<ProgressBar
						class="poll-card-option-progress"
						:percent="option.percent"
						size="8px"
					/>
				</div>
			</template>
		</RadioSelect>
		<div
			class="poll-card-footer"
			:class="{ clickable: canViewResults }"
			v-text="footerText"
			@click.stop.prevent="onClickResults"
		/>
		<PollResults
			v-if="showResultsModal"
			:pollId="pollId"
			@close="showResultsModal = false"
			@cancel="showResultsModal = false"
		/>
	</div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import { EyeSlashIcon } from '@/icons'
import {
	ProgressBar,
	RadioSelect,
	useAlert,
	TranslationGlobe,
} from '@/components/common'
import { saveVotes, translatePoll } from '@/api/poll'
import { getPoll, setPoll, updatePoll } from '@/store/messages'
import PollResults from '@/components/Polls/PollResults'
import { isCanceled } from '@/helpers/api'
import PollCloseState from './PollCloseState'
import debounce from 'debounce'
import { pollStates as states } from '@/helpers/constants'
import { getMyUser } from '@/store'
import { useI18n } from 'vue-i18n'
import * as translations from '@/store/translations'
import { scrubHighlight } from '@/components/Search/searchUtils'
import { canViewResults as canViewResultsHelper } from '@/helpers/polls'

const { t } = useI18n()

let abortCtrl = null

const props = defineProps({
	pollId: {
		type: [Number, String],
		required: true,
	},
	highlights: {
		type: Object,
		default: null,
	},
})

const alert = useAlert()

const poll = ref(getPoll(props.pollId) || {})
const showResultsModal = ref(false)
const selectModel = ref(getSelectedIds())

const state = ref(null)
const isOpen = computed(() => state.value === states.OPEN)
const isClosed = computed(() => state.value === states.CLOSED)
const isAnonymous = computed(
	() => poll.value.chat_voter_visibility === 'system'
)
const disabled = computed(() => !isOpen.value)
const options = computed(() => {
	const total =
		poll.value.options?.reduce((sum, o) => sum + o.vote_count, 0) ?? 0
	const mappedOptions =
		poll.value.options?.map((option) => {
			if (!canViewResults.value) {
				// if user cannot view results, don't display progress or vote count
				return {
					value: option.id,
					label: option.text,
					translations: option.translations,
				}
			}
			// otherwise, calculate correct progress and vote count
			const isSelected = poll.value.allows_multiple_votes
				? selectModel.value.includes(option.id)
				: selectModel.value === option.id
			const count = option.vote_count + (isSelected && !option.voted ? 1 : 0)
			return {
				value: option.id,
				label: option.text,
				translations: option.translations,
				// this displays an optimistic +1 for the vote count as soon as the user
				// clicks it while the api request to update the poll object is happening
				count,
				percent: Math.round((count / total) * 100) || 0,
			}
		}) ?? []

	// Sort options when poll is closed: first by vote count (descending), then by id (ascending)
	if (isClosed.value) {
		return mappedOptions.sort((a, b) => {
			// First sort by vote count (descending - highest first)
			const countDiff = (b.count || 0) - (a.count || 0)
			if (countDiff !== 0) return countDiff

			// If vote counts are equal, sort by id (ascending)
			return a.value - b.value
		})
	}

	return mappedOptions
})
const canViewResults = computed(() => canViewResultsHelper(poll.value))
const footerText = computed(() => {
	// default text if user can view results
	if (canViewResults.value) return t('polls_view_results_button')
	// user can't view results if they haven't voted
	if (poll.value.reveal_timing === 'after_vote')
		return t('polls_results_available_after_voting')
	// otherwise, results are available after the poll closes
	return t('polls_results_available_after_close')
})

const isTranslating = ref(false)

const canTranslate = computed(() => {
	if (!poll.value?.language) return false
	const me = getMyUser()
	const myLangs = [me.language, ...(me.languages || [])]
	return !myLangs.includes(poll.value.language)
})

const displayedQuestion = computed(() => {
	if (props.highlights) {
		const { language } = getMyUser()
		const { poll: pollHighlights } = props.highlights

		// Determine display language
		let displayLang = poll.value?.language

		if (pollHighlights) {
			// Check if we have translated highlights in preferred language
			if (pollHighlights.questionlangs?.[language]?.[0]) {
				displayLang = language
				return scrubHighlight(pollHighlights.questionlangs[language][0])
			}
			// Check for highlights in original language
			else if (
				displayLang === poll.value?.language &&
				pollHighlights.question?.[0]
			) {
				return scrubHighlight(pollHighlights.question[0])
			}
		}
	}

	if (poll.value?.isTranslated && poll.value?.translations) {
		const me = getMyUser()
		const translation = poll.value.translations.find(
			(t) => t.language === me.language
		)
		return translation?.translation || poll.value?.question
	}
	return poll.value?.question
})

const shouldTranslatePoll = computed(() => {
	if (!props.highlights?.poll) return false

	const { language } = getMyUser()
	const { poll: pollHighlights } = props.highlights

	// Check if ANY highlights are in our preferred language
	const hasQuestionInPreferredLang =
		pollHighlights.questionlangs?.[language]?.length > 0
	const hasOptionsInPreferredLang = pollHighlights.options?.some(
		(option) => option.textlangs?.[language]?.length > 0
	)

	// If we have highlights in our preferred language, translate the whole poll
	return hasQuestionInPreferredLang || hasOptionsInPreferredLang
})

const getDisplayedOptionText = (option) => {
	// Check for option highlights first
	if (props.highlights?.poll?.options) {
		const { language } = getMyUser()
		const optionHighlight = props.highlights.poll.options.find(
			(highlight) => highlight.id === option.value // option.value is the option ID
		)

		if (optionHighlight) {
			// Check for highlights in user's preferred language first
			if (optionHighlight.textlangs?.[language]?.[0]) {
				return scrubHighlight(optionHighlight.textlangs[language][0])
			}
			// Check for highlights in the original text
			else if (optionHighlight.text?.[0]) {
				return scrubHighlight(optionHighlight.text[0])
			}
		}
	}

	// Fall back to existing translation logic
	if (poll.value?.isTranslated && option?.translations) {
		const me = getMyUser()
		const translation = option.translations.find(
			(t) => t.language === me.language
		)
		return translation?.translation || option.label
	}
	return option?.label
}

const isOptionTranslated = (option) => {
	if (!poll.value?.isTranslated || !option?.translations) return false
	const me = getMyUser()
	const translation = option.translations.find(
		(t) => t.language === me.language
	)
	return !!translation
}

watch(
	[shouldTranslatePoll],
	() => {
		if (shouldTranslatePoll.value) {
			poll.value.isTranslated = true
		}
		// Don't touch isTranslated when shouldTranslatePoll is false - let normal logic handle it
	},
	{ immediate: true }
)

watch(poll, () => {
	if (shouldTranslatePoll.value) {
		poll.value.isTranslated = true
	}
	selectModel.value = getSelectedIds()
	if (canTranslate.value && poll.value?.translations?.length) {
		const me = getMyUser()
		const hasManualOverride = translations.check(props.pollId, false, true)
		const shouldTranslate = hasManualOverride
			? !me.autotranslate
			: me.autotranslate

		if (poll.value?.isTranslated !== shouldTranslate) {
			poll.value = updatePoll(props.pollId, { isTranslated: shouldTranslate })
		}
	}
})

function getSelectedIds() {
	const selectedOptionIds =
		poll.value.options?.filter((o) => o.voted)?.map((o) => o.id) ?? []
	return poll.value.allows_multiple_votes
		? selectedOptionIds
		: selectedOptionIds[0]
}

function onClickResults() {
	if (!canViewResults.value) return
	showResultsModal.value = true
}

function onPollChange(updatedPoll) {
	poll.value = { ...updatedPoll }
}

function onStateChange(updatedState) {
	state.value = updatedState
}

async function onVoteChange(votes, prevVotes) {
	if (isClosed.value) return // don't allow votes after the poll is closed
	if (!Array.isArray(votes)) {
		// Convert single values to array, but handle null/undefined as empty array
		votes = votes == null ? [] : [votes]
	}
	// optimistic update
	poll.value.options.forEach((option) => applyVoteChange(option, votes))
	setPoll({ ...poll.value })
	// debounce the save to prevent multiple rapid votes
	debouncedSaveCurrentVotes(votes, prevVotes)
}

async function saveCurrentVotes(votes, prevVotes) {
	try {
		if (abortCtrl?.abort) abortCtrl.abort()
		abortCtrl = new AbortController()
		await saveVotes(poll.value.id, votes, abortCtrl.signal)
		abortCtrl = null
	} catch (e) {
		if (isCanceled(e)) return null
		console.error(e)
		alert?.error(t('polls_vote_save_error_title'))
		// in the case of an error, reset to the user's previous vote
		selectModel.value = prevVotes
		if (!Array.isArray(prevVotes)) prevVotes = [prevVotes]
		poll.value.options.forEach((option) => applyVoteChange(option, prevVotes))
		setPoll({ ...poll.value })
	}
}
const debouncedSaveCurrentVotes = debounce(saveCurrentVotes, 1000)

function applyVoteChange(option, voteIds) {
	// based on the new list of vote ids, set the proper state of each option
	const voted = voteIds.includes(option.id)
	if (voted && !option.voted) {
		option.voted = true
		option.vote_count += 1
	} else if (!voted && option.voted) {
		option.voted = false
		option.vote_count -= 1
	}
}

const onGlobeClick = async () => {
	if (isTranslating.value) return
	isTranslating.value = true

	if (translations.check(props.pollId, false, true)) {
		translations.remove(props.pollId, false, true)
	} else {
		translations.add(props.pollId, false, true)
	}

	try {
		const translatedPoll = await translatePoll(props.pollId)
		poll.value = translatedPoll
	} catch (error) {
		console.error('Translation failed:', error)
		alert?.error(t('common_api_error_save'))
	} finally {
		isTranslating.value = false
	}
}
</script>

<style lang="scss" scoped>
@import 'src/sass/variables';

.poll-card {
	@include radius(12px);
	@include flex-column(16px);
	background-color: var(--neutral-100);
	width: 340px;

	&.closed {
		:deep(.pronto-progress-bar) {
			background-color: var(--neutral-600);
		}
	}

	.poll-card-header {
		padding: 16px 16px 0 16px;
		@include flex-column(6px);
		.poll-card-question-container {
			align-items: center;
			max-width: max-content;
			@include fontFamily(800);
			font-size: 16px;
			color: var(--neutral-800);
			&.translated {
				background-color: rgba(73, 164, 218, 0.15);
				border-radius: 8px;
				padding: 4px 6px;
				margin: -4px -6px;
			}
			.poll-card-question {
				flex: 1;
			}
			.poll-translation-globe {
				vertical-align: text-bottom;
				font-size: 13px;
				color: var(--neutral-400);
			}
		}
		.poll-card-details {
			@include flex(8px);
			@include fontFamily(500);
			font-size: 14px;
			color: var(--neutral-600);
			.poll-card-details-divider {
				border-left: 1px solid var(--neutral-300);
				height: 14px;
			}
		}
		.poll-card-anonymous {
			@include flex(8px);
			font-size: 14px;
			@include fontFamily(500);
			color: var(--neutral-600);
			.pronto-icon {
				font-size: 16px;
			}
		}
	}
	.poll-card-options {
		width: 100%;
		padding: 0 16px;
		.poll-card-option {
			@include flex-column(4px);
			width: 100%;
			.poll-card-option-text {
				@include flex-spread(8px);
				.poll-card-option-label {
					flex-grow: 1;
					font-size: 14px;
					color: var(--neutral-800);
					@include fontFamily(500);
				}
				.poll-card-option-count {
					font-size: 14px;
					color: var(--neutral-800);
					@include fontFamily(500);
				}
			}
			.poll-card-option-progress {
				width: 100%;
				flex-shrink: 0;
			}
		}
	}
	.poll-card-footer {
		@include flex-center;
		color: var(--neutral-600);
		border-top: 1px solid var(--neutral-200);
		border-radius: 0 0 12px 12px;
		overflow: hidden;
		padding: 16px 42px;
		text-align: center;
		@include fontFamily(500);
		&.clickable {
			cursor: pointer;
			&:hover {
				background-color: var(--neutral-200);
			}
		}
	}
}

.poll-card-option-label {
	&.translated {
		background-color: rgba(73, 164, 218, 0.15);
		border-radius: 8px;
		padding: 2px 4px;
		margin: -2px -4px;
		max-width: max-content;
	}
}

:deep(.pronto-radio-select-option) {
	&:hover {
		.pronto-radio-select-unchecked.pronto-icon {
			color: var(--teal-300) !important;
		}
	}
}
</style>

<style lang="scss">
@import 'src/sass/variables';

.message-wrapper {
	&:hover {
		.message-poll-card {
			background-color: var(--neutral-0);
		}
	}
	.message-poll-card {
		background-color: var(--neutral-100);
	}
}

@include overrideScheme('dark') {
	.message-wrapper {
		&:hover {
			.message-poll-card {
				background-color: var(--neutral-100);
			}
		}
		.message-poll-card {
			background-color: var(--neutral-0);
		}
	}
}
</style>
